import React from "react";

/**
 * Top-level React error boundary.
 *
 * Store reviewers (Google Play, Apple App Store) will randomly tap around the
 * app. An unhandled render error would produce a completely blank screen —
 * that is a common rejection reason under "crashes and bugs". This boundary
 * catches any render/lifecycle exception in the tree below it and shows a
 * friendly recovery card with a Reload button and a link to Support.
 *
 * We intentionally keep the fallback UI restricted to plain CSS + inline
 * styling so it renders even if Tailwind/i18n/context providers are what
 * threw. It must be robust in the worst case.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        // Log to browser dev tools for local diagnosis.
        console.error("[PawLink] Unhandled render error:", error, info?.componentStack);
        // Best-effort report to the backend telemetry endpoint. Never throws.
        try {
            const backend = process.env.REACT_APP_BACKEND_URL;
            if (!backend) return;
            fetch(`${backend}/api/telemetry/client-error`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                keepalive: true,
                body: JSON.stringify({
                    message: (error && (error.message || String(error))) || "unknown",
                    stack: error && error.stack ? String(error.stack) : "",
                    component_stack: info && info.componentStack ? String(info.componentStack) : "",
                    url: (typeof window !== "undefined" && window.location) ? String(window.location.href) : "",
                    user_agent: (typeof navigator !== "undefined") ? String(navigator.userAgent || "") : "",
                    app_version: process.env.REACT_APP_BUILD_ID || "dev",
                }),
            }).catch(() => { /* swallow */ });
        } catch { /* swallow */ }
    }

    handleReload = () => {
        try { window.location.reload(); } catch { /* noop */ }
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <div
                role="alert"
                data-testid="app-error-boundary"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px",
                    background: "#0B0D10",
                    color: "#F5EFE6",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
            >
                <div style={{ maxWidth: "440px", textAlign: "center" }}>
                    <div style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "16px",
                        background: "rgba(248,95,30,0.15)",
                        border: "1px solid rgba(248,95,30,0.35)",
                        margin: "0 auto 20px",
                        display: "grid",
                        placeItems: "center",
                        color: "#F85F1E",
                        fontSize: "28px",
                        fontWeight: 700,
                    }}>!</div>
                    <h1 style={{ fontSize: "24px", margin: "0 0 12px", fontWeight: 600 }}>
                        Something went wrong
                    </h1>
                    <p style={{ fontSize: "15px", lineHeight: 1.6, opacity: 0.75, margin: "0 0 24px" }}>
                        PawLink hit an unexpected error. It&apos;s been logged and we&apos;ll take a look.
                        Reload the page to try again — your account and pets are safe.
                    </p>
                    <button
                        type="button"
                        onClick={this.handleReload}
                        data-testid="app-error-reload"
                        style={{
                            background: "#F85F1E",
                            color: "#fff",
                            border: 0,
                            borderRadius: "999px",
                            padding: "12px 28px",
                            fontWeight: 600,
                            fontSize: "15px",
                            cursor: "pointer",
                        }}
                    >
                        Reload PawLink
                    </button>
                    <div style={{ marginTop: "20px", fontSize: "13px", opacity: 0.55 }}>
                        Still stuck?{" "}
                        <a href="/support" style={{ color: "#F85F1E", textDecoration: "underline" }}>
                            Contact support
                        </a>
                    </div>
                </div>
            </div>
        );
    }
}
